import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  effect,
  EventEmitter,
  inject,
  Input,
  Output,
} from '@angular/core';
import { AccordionComponent } from '../../../../components/accordion/accordion.component';
import { ButtonComponent } from '../../../../components/button/button.component';
import { ExpansionDirective } from '../../../../components/accordion/expansion.directive';
import { FormFieldComponent } from '../../../../components/form-field/form-field.component';
import { InputDirective } from '../../../../components/form-field/input.directive';
import { SvgIconComponent } from '@ngneat/svg-icon';
import { TranslocoDirective } from '@jsverse/transloco';
import { Filters, FilterType } from '../../../../types/filters.type';
import { FormsModule } from '@angular/forms';
import { FiltersService } from '../filters.service';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { BreakpointObserver } from '@angular/cdk/layout';
import { map } from 'rxjs';
import { SortOption } from '../sort.type';
import { SORT_OPTION_DEFAULT } from '../product-list.options';

@Component({
  selector: 'app-filters',
  imports: [
    AccordionComponent,
    ButtonComponent,
    ExpansionDirective,
    FormFieldComponent,
    InputDirective,
    SvgIconComponent,
    TranslocoDirective,
    FormsModule,
  ],
  templateUrl: './filters.component.html',
  styleUrls: ['./filters.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FiltersComponent {
  private _filters!: Filters;
  private readonly cdr = inject(ChangeDetectorRef);
  private breakpointObserver = inject(BreakpointObserver);
  filtersService = inject(FiltersService);

  isMobile = toSignal(
    this.breakpointObserver.observe('(min-width: 40em)').pipe(
      map((v) => !v.matches),
      takeUntilDestroyed(),
    ),
  );

  @Input({ required: true })
  set filters(v: Filters) {
    this._filters = v;
  }

  get filters(): Filters {
    return this._filters;
  }

  @Input()
  sorting: SortOption = SORT_OPTION_DEFAULT;

  @Output()
  filtersChange = new EventEmitter<SortOption>();

  @Output()
  cancelClick = new EventEmitter<void>();

  @Output()
  clear = new EventEmitter<void>();

  isOpenInType!: (FilterType & { open?: boolean })[];

  isOpenInOption!: (FilterType & { open?: boolean })[];

  constructor() {
    effect(() => {
      if (
        this.filtersService.isFiltersOpen() &&
        this.filters &&
        this.filters.productTypes &&
        this.filters.optionTypes
      ) {
        this.isOpenInType = this.filters.productTypes.map(this.checkIfIsOpen);
        this.isOpenInOption = this.filters.optionTypes.map(this.checkIfIsOpen);
        this.cdr.markForCheck();
      }
    });
  }

  onSave(): void {
    this.filtersChange.emit(this.sorting);
  }

  checkIfIsOpen = (
    item: FilterType & { open?: boolean },
  ): FilterType & { open?: boolean } => {
    item.open = item.values.some((item) => item.isSelected);

    return item;
  };

  shouldBeOpen(item: FilterType): boolean {
    if (!this.isOpenInType || !this.isOpenInOption) {
      return false;
    }

    const findType = this.isOpenInOption.find((option) => option.name === item.name)
      ? this.isOpenInOption.find((option) => option.name === item.name)
      : this.isOpenInType.find((option) => option.name === item.name);

    if (!findType) {
      return false;
    }

    return !!findType.open;
  }
}
